import "./App.css";
import "./App_Professional.css";
import logo from "./assets/logo.png";
import React, { useEffect, useState, useRef } from "react";
import ReactDatePicker from "./ReactDatePicker";
import axios from "axios";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard } from "lucide-react";
import QRCode from "qrcode";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import MemberPage from "./MemberPage";
import PhoneCamera from "./PhoneCamera";
import { Routes, Route } from "react-router-dom";
import Admin from "./Admin";
import { generateA4 } from "./utils/generateA4";
import SignatureCanvas from "react-signature-canvas";
import { startCamera, capturePhoto, stopCamera } from "./utils/camera";

import MemberLogin from "./MemberLogin";
import MemberPortal from "./MemberPortal";
import Home from "./Home";
import AdminLogin from "./AdminLogin";
import AdminRoute from "./AdminRoute";
import MemberRoute from "./MemberRoute";

import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import {
  ProtectedAdminRoute,
  ProtectedMemberRoute,
  ProtectedSuperAdminRoute,
} from "./ProtectedRoutes";
import SuperAdmin from "./SuperAdmin";
import InstallAppPrompt from "./InstallAppPrompt";

axios.defaults.withCredentials = true;

function App() {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("admin");
    localStorage.removeItem("member");
    navigate("/");
  };

  const [lastId, setLastId] = useState(null);

  const initialValues = {
    nom: "",
    prenom: "",
    gender: "",
    email: "",
    contact: "",
    adresse: "",
    codePostal: "",
    commune: "",
    metier: "",
    hebergement: "",
    photo: null,
    typeAdhesion: [],
    adhesion: new Date(),
    dateExpiration: (() => {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      return date;
    })(),
    cotisation: "",
    cotisationStatus: "",
    cotisationAmount: "",
    naissance: "",
    commentaire: "",
    numero_dossier: "",
  };

  const [values, setValues] = useState(initialValues);
  const [communeSuggestions, setCommuneSuggestions] = useState([]);
  const [communeLookupMessage, setCommuneLookupMessage] = useState("");

  // Look up French communes from the entered postal code.
  useEffect(() => {
    const postalCode = String(values.codePostal || "").trim();

    if (!/^\d{5}$/.test(postalCode)) {
      setCommuneSuggestions([]);
      setCommuneLookupMessage("");
      return;
    }

    const controller = new AbortController();
    setCommuneLookupMessage("Recherche de la commune…");

    fetch(
      `https://geo.api.gouv.fr/communes?codePostal=${encodeURIComponent(postalCode)}&fields=nom,code,codesPostaux&format=json`,
      { signal: controller.signal },
    )
      .then((response) => {
        if (!response.ok) throw new Error("La recherche a échoué");
        return response.json();
      })
      .then((communes) => {
        const names = [
          ...new Set((communes || []).map((item) => item.nom).filter(Boolean)),
        ];
        setCommuneSuggestions(names);

        if (names.length === 1) {
          setValues((prev) => ({ ...prev, commune: names[0] }));
          setCommuneLookupMessage("");
        } else if (names.length > 1) {
          setCommuneLookupMessage(
            "Plusieurs communes correspondent à ce code postal. Choisissez la bonne :",
          );
        } else {
          setCommuneLookupMessage(
            "Aucune commune trouvée pour ce code postal.",
          );
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setCommuneSuggestions([]);
          setCommuneLookupMessage(
            "Recherche indisponible. Vous pouvez saisir la commune manuellement.",
          );
        }
      });

    return () => controller.abort();
  }, [values.codePostal]);

  // Birth date is selected independently: day, month, then year.
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [birthdateError, setBirthdateError] = useState("");

  // Number of valid days for the currently selected month and year.
  // If either is not selected yet, show all 31 possible day options.
  const daysInSelectedMonth =
    birthMonth && birthYear
      ? new Date(Number(birthYear), Number(birthMonth), 0).getDate()
      : 31;

  const updateBirthdate = (day, month, year) => {
    // Keep month and year selections independent.
    setBirthMonth(month);
    setBirthYear(year);

    let validDay = day;
    setBirthdateError("");

    // Tell the user exactly what needs correcting when the selected day
    // does not exist in the chosen month/year (e.g. 31 February).
    if (validDay && month && year) {
      const maxDay = new Date(Number(year), Number(month), 0).getDate();
      if (Number(validDay) > maxDay) {
        const monthNames = [
          "janvier",
          "février",
          "mars",
          "avril",
          "mai",
          "juin",
          "juillet",
          "août",
          "septembre",
          "octobre",
          "novembre",
          "décembre",
        ];
        const selectedMonth = monthNames[Number(month) - 1];
        setBirthdateError(
          `La date sélectionnée n'existe pas : ${validDay} ${selectedMonth} ${year}. Ce mois comporte ${maxDay} jour${maxDay > 1 ? "s" : ""}. Veuillez choisir un jour entre 1 et ${maxDay}.`,
        );
        validDay = "";
      }
    }

    setBirthDay(validDay);

    // Save only a complete, valid date in YYYY-MM-DD format.
    if (!validDay || !month || !year) {
      setValues((prev) => ({ ...prev, naissance: "" }));
      return;
    }

    setValues((prev) => ({
      ...prev,
      naissance: `${year}-${month}-${validDay}`,
    }));
  };

  const [respValues, setRespValues] = useState({
    message: "",
    type: "",
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const photoRef = useRef();

  // IMAGE CROPPING
  const [cropPhoto, setCropPhoto] = useState(null);
  const [crop, setCrop] = useState(null);

  const cropImageRef = useRef(null);

  // SIGNATURES
  const sigRef = useRef();
  const adminSigRef = useRef();

  // CAMERA
  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // PHONE CAMERA / QR
  const [showPhoneCamera, setShowPhoneCamera] = useState(false);
  const [phoneCameraToken, setPhoneCameraToken] = useState(null);
  const [phoneCameraQr, setPhoneCameraQr] = useState("");
  const [phoneCameraStatus, setPhoneCameraStatus] = useState("");
  const [phoneCameraPurpose, setPhoneCameraPurpose] = useState("photo");

  // PAPER SIGNATURE
  const [signaturePhoto, setSignaturePhoto] = useState(null);
  const [signatureCrop, setSignatureCrop] = useState(null);
  const signatureCropImageRef = useRef(null);
  const [memberSignaturePreview, setMemberSignaturePreview] = useState(null);

  const [showSuiviModal, setShowSuiviModal] = useState(false);
  const [suivi, setSuivi] = useState([]);

  const [showSuiviPopup, setShowSuiviPopup] = useState(false);

  // GET CSRF
  const [csrfToken, setCsrfToken] = useState("");

  useEffect(() => {
    axios
      .get("/api/getCSRF.php", {
        withCredentials: true,
      })
      .then((res) => {
        console.log("CSRF RESPONSE:", res.data);
        setCsrfToken(res.data.csrf_token);
      })
      .catch((err) => {
        console.error("CSRF ERROR:", err);
      });
  }, []);

  const [formData, setFormData] = useState({
    suivi_administratif: [],
  });

  // OPEN PHONE CAMERA / GENERATE QR
  const openPhoneCamera = async (purpose = "photo") => {
    const token =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
            .toString(36)
            .slice(2)}`;

    const phoneUrl = `${window.location.origin}/phone-camera?token=${encodeURIComponent(
      token,
    )}`;

    try {
      const qrDataUrl = await QRCode.toDataURL(phoneUrl, {
        width: 260,
        margin: 2,
      });

      setPhoneCameraToken(token);
      setPhoneCameraQr(qrDataUrl);
      setPhoneCameraPurpose(purpose);
      setPhoneCameraStatus(
        purpose === "signature"
          ? "✍️ Scannez le QR code, signez sur papier blanc puis photographiez la signature."
          : "📱 Scannez le QR code avec le téléphone puis prenez la photo.",
      );
      setShowPhoneCamera(true);
    } catch (error) {
      console.error("PHONE CAMERA QR ERROR:", error);
      alert("Impossible de générer le QR code.");
    }
  };

  const closePhoneCamera = () => {
    setShowPhoneCamera(false);
    setPhoneCameraToken(null);
    setPhoneCameraQr("");
    setPhoneCameraStatus("");
    setPhoneCameraPurpose("photo");
  };

  // WAIT FOR PHOTO SENT FROM PHONE
  useEffect(() => {
    if (!showPhoneCamera || !phoneCameraToken) return;

    let stopped = false;
    let intervalId = null;

    const checkPhonePhoto = async () => {
      try {
        const response = await axios.get(
          `/api/phoneCameraCheck.php?token=${encodeURIComponent(
            phoneCameraToken,
          )}`,
          { withCredentials: true },
        );

        if (stopped) return;

        const data = response.data;

        if (!data || !data.success) {
          setPhoneCameraStatus("❌ Erreur lors de la vérification.");
          return;
        }

        if (!data.uploaded) {
          return;
        }

        // The check endpoint now returns the image itself as a base64 data URL.
        // This avoids a second browser request to /api/phone_camera_uploads/...
        // and makes the phone photo behave exactly like a normal PC file.
        if (!data.image) {
          throw new Error(
            "La photo a été trouvée mais son contenu est introuvable.",
          );
        }

        setPhoneCameraStatus("⏳ Photo reçue, préparation du recadrage...");

        // Parse the data URL without a regular expression.
        // This avoids escaping problems with JavaScript regex literals.
        const commaIndex = data.image.indexOf(",");

        if (commaIndex <= 5 || !data.image.startsWith("data:image/")) {
          throw new Error("Format de photo reçu invalide.");
        }

        const header = data.image.slice(0, commaIndex);
        const base64 = data.image.slice(commaIndex + 1);

        const mimeMatch = header.match(/^data:(image\/[^;]+);base64$/);

        if (!mimeMatch || !base64) {
          throw new Error("Format de photo reçu invalide.");
        }

        const mimeType = mimeMatch[1];

        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        const extension =
          mimeType === "image/png"
            ? "png"
            : mimeType === "image/webp"
              ? "webp"
              : "jpg";

        const file = new File(
          [bytes],
          `phone-photo-${Date.now()}.${extension}`,
          { type: mimeType },
        );

        const reader = new FileReader();

        reader.onloadend = () => {
          if (stopped) return;

          if (phoneCameraPurpose === "signature") {
            setSignaturePhoto({
              file,
              preview: reader.result,
              name: file.name,
            });
            setSignatureCrop(null);
            closePhoneCamera();
            return;
          }

          setCropPhoto({
            file,
            preview: reader.result,
            name: file.name,
          });

          setCrop(null);
          setPhoneCameraStatus("✅ Photo reçue.");
          closePhoneCamera();
        };

        reader.readAsDataURL(file);
      } catch (error) {
        if (!stopped) {
          console.error("PHONE CAMERA CHECK ERROR:", error);
          setPhoneCameraStatus(
            "⚠️ Connexion en attente... Vérification automatique.",
          );
        }
      }
    };

    checkPhonePhoto();
    intervalId = window.setInterval(checkPhonePhoto, 1500);

    return () => {
      stopped = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [showPhoneCamera, phoneCameraToken, phoneCameraPurpose]);

  const handleSignatureImageLoad = (e) => {
    const image = e.currentTarget;

    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 80,
        },
        1 / 3,
        image.width,
        image.height,
      ),
      image.width,
      image.height,
    );

    setSignatureCrop(initialCrop);
  };

  // Convert the cropped paper signature into a clean transparent PNG.
  // Uses adaptive background detection so normal phone photos of white paper
  // do not become a solid black rectangle.
  const createDigitalSignature = async () => {
    if (!signaturePhoto || !signatureCropImageRef.current || !signatureCrop) {
      return;
    }

    const image = signatureCropImageRef.current;

    const pixelCrop = {
      x: Math.max(0, Math.round((signatureCrop.x / 100) * image.naturalWidth)),
      y: Math.max(0, Math.round((signatureCrop.y / 100) * image.naturalHeight)),
      width: Math.max(
        1,
        Math.round((signatureCrop.width / 100) * image.naturalWidth),
      ),
      height: Math.max(
        1,
        Math.round((signatureCrop.height / 100) * image.naturalHeight),
      ),
    };

    const canvas = document.createElement("canvas");
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!ctx) return;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const pixels = imageData.data;
    const luminances = [];

    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];

      luminances.push(0.299 * r + 0.587 * g + 0.114 * b);
    }

    luminances.sort((a, b) => a - b);

    const backgroundIndex = Math.min(
      luminances.length - 1,
      Math.floor(luminances.length * 0.9),
    );

    const backgroundLuminance = luminances[backgroundIndex];

    const threshold = Math.max(90, Math.min(225, backgroundLuminance - 28));

    let minX = canvas.width;
    let minY = canvas.height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const i = (y * canvas.width + x) * 4;

        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];

        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

        const darkness = threshold - luminance;

        let alpha = 0;

        if (darkness > 0) {
          alpha = Math.min(
            255,
            Math.round((darkness / Math.max(1, threshold)) * 255),
          );
        }

        if (alpha < 18) {
          alpha = 0;
        }

        pixels[i] = 0;
        pixels[i + 1] = 0;
        pixels[i + 2] = 0;
        pixels[i + 3] = alpha;

        if (alpha > 35) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (maxX < 0 || maxY < 0) {
      toast.error(
        "Signature non détectée. Cadrez bien la signature sur le papier.",
      );
      return;
    }

    ctx.putImageData(imageData, 0, 0);

    const padding = Math.max(
      8,
      Math.round(Math.min(canvas.width, canvas.height) * 0.04),
    );

    const finalX = Math.max(0, minX - padding);
    const finalY = Math.max(0, minY - padding);
    const finalRight = Math.min(canvas.width, maxX + padding + 1);
    const finalBottom = Math.min(canvas.height, maxY + padding + 1);

    const finalCanvas = document.createElement("canvas");

    finalCanvas.width = Math.max(1, finalRight - finalX);
    finalCanvas.height = Math.max(1, finalBottom - finalY);

    const finalCtx = finalCanvas.getContext("2d");

    if (!finalCtx) return;

    finalCtx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);

    finalCtx.drawImage(
      canvas,
      finalX,
      finalY,
      finalCanvas.width,
      finalCanvas.height,
      0,
      0,
      finalCanvas.width,
      finalCanvas.height,
    );

    const pngDataUrl = finalCanvas.toDataURL("image/png");

    setMemberSignaturePreview(pngDataUrl);
    setSignaturePhoto(null);
    setSignatureCrop(null);

    toast.success("Signature numérisée avec succès ✅");
  };

  const handleCropImageLoad = (e) => {
    const image = e.currentTarget;

    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        3 / 4,
        image.width,
        image.height,
      ),
      image.width,
      image.height,
    );

    setCrop(initialCrop);
  };

  const createCroppedPhoto = async () => {
    if (!cropPhoto || !cropImageRef.current || !crop) return;

    const image = cropImageRef.current;

    const pixelCrop = {
      x: (crop.x / 100) * image.naturalWidth,
      y: (crop.y / 100) * image.naturalHeight,
      width: (crop.width / 100) * image.naturalWidth,
      height: (crop.height / 100) * image.naturalHeight,
    };

    const canvas = document.createElement("canvas");

    canvas.width = Math.round(pixelCrop.width);
    canvas.height = Math.round(pixelCrop.height);

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const croppedFile = new File(
          [blob],
          cropPhoto.name || "cropped-photo.jpg",
          {
            type: "image/jpeg",
          },
        );

        const croppedPreview = canvas.toDataURL("image/jpeg", 0.92);

        setValues((prev) => ({
          ...prev,
          photo: croppedFile,
        }));

        setPhotoPreview(croppedPreview);

        setCropPhoto(null);
      },
      "image/jpeg",
      0.92,
    );
  };

  // INPUT HANDLER
  const handleChanges = (e) => {
    const { name, type, value, checked, files } = e.target;

    // PHOTO UPLOAD
    if (type === "file") {
      const file = files && files[0];

      if (file) {
        const reader = new FileReader();

        reader.onloadend = () => {
          setCropPhoto({
            file,
            preview: reader.result,
            name: file.name,
          });

          setCrop(null);
        };

        reader.readAsDataURL(file);
      }

      return;
    }

    // When the postal code changes, clear a previous commune until it is looked up again.
    if (name === "codePostal") {
      setValues((prev) => ({ ...prev, codePostal: value, commune: "" }));
      setCommuneSuggestions([]);
      setCommuneLookupMessage("");
      return;
    }

    // HANDLE CHECKBOX
    if (type === "checkbox") {
      setValues((prev) => {
        const current = prev[name] || [];

        if (checked) {
          return {
            ...prev,
            [name]: [...current, value],
          };
        }

        return {
          ...prev,
          [name]: current.filter((v) => v !== value),
        };
      });

      return;
    }

    // NORMAL INPUTS
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // FORMATING DATE
  const formatDate = (date) => {
    if (!date || date === "0000-00-00") return "";

    const d = date instanceof Date ? date : new Date(date);

    if (isNaN(d.getTime())) return "";

    return d.toISOString().split("T")[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // COTISATION IS OPTIONAL.
    // Validate payment details only when a cotisation amount was selected.
    if (values.cotisation === "20" || values.cotisation === "50") {
      if (!values.cotisationStatus) {
        alert(
          "Veuillez préciser le statut de paiement de la cotisation, ou laissez la cotisation vide.",
        );
        return;
      }

      if (values.cotisationStatus === "partiel") {
        const paid = Number(values.cotisationAmount || 0);
        const chosen = Number(values.cotisation || 0);

        if (paid < 0 || paid > chosen) {
          alert(`Le montant payé doit être compris entre 0 € et ${chosen} €.`);
          return;
        }
      }
    }

    if (!csrfToken) {
      alert("⛔ CSRF not loaded yet");
      return;
    }

    console.log("CSRF SENT:", csrfToken);

    const formData = new FormData();

    Object.keys(values).forEach((key) => {
      const val = values[key];

      if (Array.isArray(val)) {
        formData.append(key, val.join(", "));
      } else if (val instanceof Date && !isNaN(val.getTime())) {
        formData.append(key, formatDate(val));
      } else if (val !== null) {
        formData.append(key, val);
      }
    });

    formData.append("csrf_token", csrfToken);

    console.log("FORMDATA CSRF:", formData.get("csrf_token"));

    formData.append("suivi_administratif", JSON.stringify(suivi));

    if (memberSignaturePreview) {
      formData.append("signature", memberSignaturePreview);
    } else if (sigRef.current && !sigRef.current.isEmpty()) {
      const signatureData = sigRef.current.toDataURL("image/png");

      formData.append("signature", signatureData);
    }

    const currentPhoto = photoPreview;

    let toastId;
    let printWindow = null;

    // Open the print tab while still inside the submit click. This avoids
    // popup blockers after the asynchronous registration/PDF generation steps.
    // It will only be populated with the PDF after the server confirms success.
    try {
      printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(
          '<!doctype html><html><head><title>Préparation de la fiche</title></head><body style="font-family:Arial,sans-serif;padding:24px">Préparation de la fiche d’adhésion pour impression…</body></html>',
        );
        printWindow.document.close();
      }
    } catch (popupError) {
      console.warn("PRINT WINDOW COULD NOT OPEN:", popupError);
    }

    try {
      let response;
      let password;

      try {
        response = await axios.post("/api/addMember.php", formData, {
          withCredentials: true,
        });

        password = response.data.password;
      } catch (error) {
        console.error("FULL ERROR:", error);

        if (printWindow && !printWindow.closed) printWindow.close();

        if (error.response) {
          console.log("SERVER RESPONSE:", error.response.data);

          alert(JSON.stringify(error.response.data));
        } else {
          alert("Erreur inconnue");
        }

        return;
      }

      // IMPORTANT CHECK
      if (!response.data || !response.data.success) {
        if (printWindow && !printWindow.closed) printWindow.close();
        alert("❌ Enregistrement échoué. Aucun PDF généré.");

        return;
      }

      // SUCCESS
      const numero = response.data.numero_membre;

      const id = response.data.id;

      toast.success(
        (t) => (
          <span>
            Inscription réussie - N° {numero}{" "}
            <button
              style={{
                marginLeft: "10px",
                background: "white",
                color: "#ff6600",
                border: "none",
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => {
                navigate(`/member/${id}`);
                toast.dismiss(t.id);
              }}
            >
              🔍 Voir
            </button>
          </span>
        ),
        { id: toastId },
      );

      toast.success("Membre ajouté ✅");

      setLastId(response.data.id);

      setRespValues({
        message: `Inscription réussie - N° ${numero}`,
        type: "success",
      });

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      let signatureBase64 = memberSignaturePreview || null;

      if (!signatureBase64 && sigRef.current && !sigRef.current.isEmpty()) {
        signatureBase64 = sigRef.current.toDataURL("image/png");
      }

      await axios.get("/api/getMembers.php");

      // FETCH ADMIN SIGNATURE
      const resSignAdmin = await axios.get("/api/getAdminSignature.php");

      const adminSignatureFile = resSignAdmin.data.signature;

      const pdfData = {
        ...values,
        numero_membre: numero,
        signature: signatureBase64,
        admin_signature_file: adminSignatureFile,
        id: id,
      };

      console.log("ADMIN SIGNATURE:", adminSignatureFile);

      await generateFullPDF(pdfData, currentPhoto, password, printWindow);

      resetForm();
    } catch (error) {
      console.error(error);
      if (printWindow && !printWindow.closed) printWindow.close();

      setRespValues({
        message: "Erreur lors de l'envoi",
        type: "error",
      });
    }
  };

  // ADHESION
  const handleAdhesionChange = (date) => {
    if (!date) return;

    // Calculate +1 year
    const expiration = new Date(date);

    expiration.setFullYear(expiration.getFullYear() + 1);

    setValues((prev) => ({
      ...prev,
      adhesion: date,
      dateExpiration: expiration,
    }));
  };
  // COTISATION / PAYMENT
  // Only one annual contribution amount can be selected.
  // cotisationAmount stores the amount actually paid.
  const handleCotisationChange = (option) => {
    setValues((prev) => ({
      ...prev,
      cotisation: option,
      cotisationStatus: "",
      cotisationAmount: "",
    }));
  };

  const handleCotisationStatusChange = (status) => {
    setValues((prev) => ({
      ...prev,
      cotisationStatus: status,
      // If the member paid in full, automatically record the full chosen amount.
      cotisationAmount:
        status === "paye" ? prev.cotisation : prev.cotisationAmount,
    }));
  };

  const getCotisationRemaining = () => {
    const chosen = Number(values.cotisation || 0);
    const paid =
      values.cotisationStatus === "paye"
        ? chosen
        : Number(values.cotisationAmount || 0);

    if (!chosen) return 0;

    return Math.max(0, chosen - paid);
  };

  // GENERATE PDF
  // FICHE D'INSCRIPTION HEADER / FOOTER
  // Embedded from the images supplied for the membership fiche.
  const ficheHeaderImage =
    "data:image/png;base64,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";
  const ficheFooterImage =
    "data:image/png;base64,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";

  const generateFullPDF = async (data, photo, password, printWindow = null) => {
    if (!data.nom || !data.prenom) {
      alert("Veuillez remplir le nom et le prénom.");

      return;
    }

    const loadImageAsBase64 = (url) =>
      new Promise((resolve) => {
        const img = new Image();

        img.crossOrigin = "anonymous";

        img.onload = () => {
          const canvas = document.createElement("canvas");

          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");

          ctx.drawImage(img, 0, 0);

          resolve(canvas.toDataURL("image/png"));
        };

        img.onerror = () => resolve(null);

        img.src = url;
      });

    let adminSignatureBase64 = null;

    if (data.admin_signature_file) {
      adminSignatureBase64 = await loadImageAsBase64(
        `/api/image.php?file=${data.admin_signature_file}`,
      );
    }

    const doc = new jsPDF();

    const orange = [255, 102, 0];

    // HEADER IMAGE
    // The supplied header is approximately 4:1, so it is fitted to the
    // printable width while preserving its original proportions.
    const headerWidth = 170;
    const headerHeight = 42.5;

    doc.addImage(ficheHeaderImage, "PNG", 20, 8, headerWidth, headerHeight);

    // YEAR
    // The year follows the member's registration/admission date.
    // Example: an adhesion date of 15/03/2026 displays "ANNÉE 2026".
    let inscriptionYear = new Date().getFullYear();

    if (data.adhesion) {
      const adhesionDate = new Date(data.adhesion);

      if (!Number.isNaN(adhesionDate.getTime())) {
        inscriptionYear = adhesionDate.getFullYear();
      } else {
        const yearMatch = String(data.adhesion).match(/(?:19|20)\d{2}/);

        if (yearMatch) {
          inscriptionYear = Number(yearMatch[0]);
        }
      }
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...orange);

    doc.text(`ANNÉE ${inscriptionYear}`, 105, 56, {
      align: "center",
    });

    // SECTION SYSTEM
    // Start below the supplied header and year.
    let y = 66;

    const section = (title) => {
      doc.setFontSize(13);

      doc.setTextColor(...orange);

      doc.text(title, 20, y);

      y += 5;

      doc.setDrawColor(200);

      doc.line(20, y, 190, y);

      y += 8;

      doc.setTextColor(0);
    };

    const field = (label, value) => {
      doc.setFontSize(11);

      doc.setTextColor(100);

      doc.text(label, 20, y);

      doc.setTextColor(0);

      const safeValue =
        value !== undefined && value !== null && value !== ""
          ? String(value)
          : "-";

      doc.text(safeValue, 80, y);

      y += 7;
    };

    // PERSONAL INFO
    section("Informations personnelles");

    field("Nom", data.nom);

    field("Prénom", data.prenom);

    field("Sexe", data.gender);

    field(
      "Date de naissance",
      data.naissance ? new Date(data.naissance).toLocaleDateString() : "-",
    );

    field("Numéro membre", data.numero_membre);

    // CONTACT
    y += 5;

    section("Coordonnées");

    field("Email", data.email);

    field("Téléphone", data.contact);

    field("Adresse", data.adresse);

    field("Code Postal", data.codePostal);

    field("Commune", data.commune);

    // PROFESSIONAL
    y += 5;

    section("Informations professionnelles");

    field("Métier", data.metier);

    field("Besoin d'hébergement", data.hebergement);

    // MEMBERSHIP
    y += 5;

    section("Adhésion");

    let adhesionTypes = "-";

    if (data.typeAdhesion) {
      try {
        const parsed = Array.isArray(data.typeAdhesion)
          ? data.typeAdhesion
          : JSON.parse(data.typeAdhesion);

        adhesionTypes = parsed
          .map((t) => {
            if (t === "adherent") return "Adhérent";

            if (t === "benevole") return "Bénévole";

            if (t === "organisateur") return "Organisateur";

            if (t === "autre") return "Autre";

            return t;
          })
          .join(", ");
      } catch {
        adhesionTypes = data.typeAdhesion;
      }
    }

    field("Type d’adhésion", adhesionTypes);

    field("Date d'adhésion", formatDate(data.adhesion));

    field("Date d'expiration", formatDate(data.dateExpiration));

    // COTISATION / PAYMENT
    y += 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...orange);

    doc.text("Cotisation annuelle", 20, y);

    y += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(0);

    const cotisationLabel =
      data.cotisation === "20"
        ? "20 € / an — faibles ressources"
        : data.cotisation === "50"
          ? "50 € / an — salarié"
          : "-";

    doc.text(`Montant à payer : ${cotisationLabel}`, 20, y);

    y += 5;

    if (data.cotisation === "20" || data.cotisation === "50") {
      const chosenAmount = Number(data.cotisation || 0);

      const paidAmount =
        data.cotisationStatus === "paye"
          ? chosenAmount
          : Number(data.cotisationAmount || 0);

      const remainingAmount = Math.max(0, chosenAmount - paidAmount);

      const paymentStatus =
        data.cotisationStatus === "paye"
          ? "Payé en totalité"
          : data.cotisationStatus === "partiel"
            ? "Paiement partiel"
            : "-";

      doc.text(`Statut : ${paymentStatus}`, 20, y);

      y += 5;

      doc.text(`Montant payé : ${paidAmount.toFixed(2)} €`, 20, y);
      doc.text(`Reste à payer : ${remainingAmount.toFixed(2)} €`, 95, y);

      y += 5;
    }

    // PHOTO
    if (photo) {
      doc.rect(140, 65, 50, 50);
      doc.addImage(photo, "JPEG", 142, 67, 46, 46);
    }

    // SIGNATURE
    // Keep a clear separation between the payment information and
    // the signature block. The signature area is also kept safely
    // above the footer so nothing overlaps.
    const signatureY = Math.min(y + 10, 264);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0);

    doc.text("Signature du membre :", 20, signatureY);

    const signatureBase64 = data.signature;

    if (signatureBase64) {
      doc.addImage(signatureBase64, "PNG", 20, signatureY + 4, 60, 12);
    } else {
      doc.line(20, signatureY + 9, 90, signatureY + 9);
    }

    doc.text("Signature de l'administration :", 110, signatureY);

    if (adminSignatureBase64) {
      doc.addImage(adminSignatureBase64, "PNG", 110, signatureY + 4, 60, 12);
    } else {
      doc.line(110, signatureY + 9, 180, signatureY + 9);
    }

    // FOOTER TEXT
    // Footer is placed below the signature images with a clear gap.
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(60);

    doc.text(
      "United Migrants · tél. : 07 83 37 69 58 · email : asso.unitedmigrants@gmail.com",
      105,
      286,
      { align: "center" },
    );

    doc.text("25 rue Rocroy · 75010 Paris", 105, 290, { align: "center" });

    doc.text("Réf. : W751231741", 105, 294, { align: "center" });

    // Generation date kept discreetly just above the footer.
    doc.setFont("helvetica", "italic");
    doc.setFontSize(6.5);
    doc.setTextColor(150);
    doc.text(`Document généré le ${new Date().toLocaleDateString()}`, 20, 284);

    // SAVE
    const blob = doc.output("blob");

    // DOWNLOAD (preserved) AND OPEN THE SAME COMPLETE FICHE FOR PRINTING.
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download = `${data.nom}_fiche.pdf`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    if (printWindow && !printWindow.closed) {
      try {
        printWindow.document.title = `Fiche d’adhésion - ${data.prenom} ${data.nom}`;
        printWindow.location.href = url;

        // Give the browser's built-in PDF viewer time to render the full PDF
        // before opening the native print dialog. A guard prevents duplicate dialogs.
        let printDialogOpened = false;
        const openPrintDialog = () => {
          if (printDialogOpened) return;
          printDialogOpened = true;
          try {
            if (!printWindow.closed) printWindow.print();
          } catch (printError) {
            console.warn("AUTOMATIC PRINT DIALOG ERROR:", printError);
          }
        };

        printWindow.onload = () => {
          window.setTimeout(openPrintDialog, 1000);
        };

        // Some PDF viewers do not trigger a normal onload event consistently.
        window.setTimeout(openPrintDialog, 1800);
      } catch (printError) {
        console.warn("COULD NOT LOAD PDF FOR PRINTING:", printError);
      }
    } else {
      toast.error(
        "La fiche a été générée, mais la fenêtre d’impression a été bloquée. Autorisez les fenêtres pop-up puis réessayez.",
      );
    }

    // Keep the object URL alive long enough for both the PDF viewer and download.
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);

    // AUTO EMAIL SEND
    // Only when an email was provided
    if (data.email && data.email.trim() !== "") {
      try {
        const formData = new FormData();

        formData.append("pdf", blob, "fiche.pdf");

        formData.append("email", data.email);

        formData.append("nom", data.nom);

        formData.append("prenom", data.prenom);

        formData.append("password", password || "");

        formData.append("dateExpiration", data.dateExpiration || "");
        formData.append("cotisation", data.cotisation || "");
        formData.append("cotisationStatus", data.cotisationStatus || "");
        formData.append("cotisationAmount", data.cotisationAmount || "");

        formData.append("id", data.id || lastId);

        const res = await axios.post("/api/sendCard.php", formData, {
          withCredentials: true,
        });

        console.log("EMAIL RESPONSE:", res.data);

        if (res.data.success) {
          toast.success("📧 Email envoyé automatiquement !");
        } else {
          toast.error("Erreur envoi email ❌");
        }
      } catch (err) {
        console.error("EMAIL ERROR:", err);

        toast.error("Erreur email ❌");
      }
    }
  };

  // GENERATE CARD
  const generateCardPDF = async (data, photo) => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [85.6, 53.98],
    });

    // BACKGROUND
    doc.setFillColor(30, 30, 30);

    doc.rect(0, 0, 85, 55, "F");

    // HEADER
    doc.setFillColor(255, 102, 0);

    doc.rect(0, 0, 85, 10, "F");

    doc.setTextColor(255);

    doc.setFontSize(10);

    doc.text("CARTE D'ADHERANT", 42, 7, {
      align: "center",
    });

    // PHOTO
    if (photo) {
      doc.addImage(photo, "JPEG", 5, 15, 20, 25);
    }

    // INFOS
    doc.setTextColor(255);

    doc.setFontSize(10);

    doc.text(`${data.nom} ${data.prenom}`, 30, 20);

    doc.setFontSize(8);

    doc.text(`N°: ${data.numero_membre}`, 30, 28);

    doc.text(
      `Valide: ${
        data.dateExpiration
          ? new Date(data.dateExpiration).toLocaleDateString()
          : "-"
      }`,
      30,
      35,
    );

    // QR CODE
    const qrData = JSON.stringify({
      id: data.numero_membre,
      nom: data.nom,
      email: data.email,
    });

    try {
      const qrImage = await QRCode.toDataURL(qrData);

      doc.addImage(qrImage, "PNG", 60, 18, 10, 10);
    } catch (err) {
      console.error("QR ERROR:", err);
    }

    // FOOTER
    doc.setFontSize(7);

    doc.setTextColor(200);

    doc.text("Association Membres", 42, 50, {
      align: "center",
    });

    doc.setDrawColor(255, 102, 0);

    doc.rect(0, 0, 85, 55);

    doc.setLineWidth(0.5);

    const blob = doc.output("blob");

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download = `${data.nom}_carte.pdf`;

    link.click();

    URL.revokeObjectURL(url);
  };

  // GENERATE 8 CARDS PER PAGE
  const generateBatchPDF = async (members) => {
    const doc = new jsPDF("portrait", "mm", "a4");

    const cardWidth = 90;
    const cardHeight = 60;

    const marginX = 10;
    const marginY = 10;

    const gapX = 5;
    const gapY = 5;

    for (let i = 0; i < members.length; i++) {
      const m = members[i];

      const col = i % 2;

      const row = Math.floor(i / 2);

      const x = marginX + col * (cardWidth + gapX);

      const y = marginY + row * (cardHeight + gapY);

      // CARD BACKGROUND
      doc.setFillColor(30, 30, 30);

      doc.rect(x, y, cardWidth, cardHeight, "F");

      // HEADER
      doc.setFillColor(255, 102, 0);

      doc.rect(x, y, cardWidth, 10, "F");

      doc.setTextColor(255);

      doc.setFontSize(10);

      doc.text("CARTE MEMBRE", x + cardWidth / 2, y + 7, {
        align: "center",
      });

      // NAME
      doc.setFontSize(9);

      doc.text(`${m.nom} ${m.prenom}`, x + 5, y + 20);

      doc.text(`N°: ${m.numero_membre}`, x + 5, y + 28);

      doc.text(
        `Valide: ${
          m.dateExpiration
            ? new Date(m.dateExpiration).toLocaleDateString()
            : "-"
        }`,
        x + 5,
        y + 35,
      );

      // QR
      const qrData = JSON.stringify({
        id: m.numero_membre,
        nom: m.nom,
      });

      const qr = await QRCode.toDataURL(qrData);

      doc.addImage(qr, "PNG", x + 60, y + 20, 20, 20);

      // BORDER
      doc.setDrawColor(255, 102, 0);

      doc.rect(x, y, cardWidth, cardHeight);
    }

    doc.save("cartes_membres.pdf");
  };

  // RESET
  const resetForm = () => {
    setCropPhoto(null);
    setSignaturePhoto(null);
    setSignatureCrop(null);
    setMemberSignaturePreview(null);
    setValues(initialValues);
    setBirthDay("");
    setBirthMonth("");
    setBirthYear("");
    setBirthdateError("");

    setPhotoPreview(null);

    setSuivi([]);

    if (photoRef.current) {
      photoRef.current.value = null;
    }

    if (sigRef.current) {
      sigRef.current.clear();
    }

    if (adminSigRef.current) {
      adminSigRef.current.clear();
    }
  };

  return (
    <>
      {/* GLOBAL ALERT SYSTEM */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#1f2937",
            color: "#fff",
            borderRadius: "8px",
            fontSize: "13px",
          },
        }}
      />

      <Routes>
        {/* HOME */}
        <Route path="/" element={<Home />} />

        {/* LOGIN */}
        <Route path="/admin-login" element={<AdminLogin />} />

        <Route path="/member-login" element={<MemberLogin />} />

        {/* FORM */}
        <Route
          path="/form"
          element={
            <ProtectedAdminRoute>
              <div className="container">
                {/* ADMIN BUTTON */}
                <div className="top-bar">
                  <button
                    type="button"
                    className="admin-btn"
                    onClick={() => navigate("/admin")}
                  >
                    <LayoutDashboard size={18} />

                    <span>Dashboard</span>
                  </button>

                  <button onClick={handleLogout} className="logout-btn">
                    Logout
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* LOGO */}
                  <div className="logo-container">
                    <img src={logo} alt="Logo" className="logo" />
                  </div>

                  <h1>Formulaire d'inscription</h1>

                  {/* NAME */}
                  <div className="input-group">
                    <input
                      type="text"
                      name="nom"
                      value={values.nom}
                      onChange={handleChanges}
                      placeholder=" "
                      required
                    />

                    <label>Nom</label>
                  </div>

                  {/* PRENOM */}
                  <div className="input-group">
                    <input
                      type="text"
                      name="prenom"
                      value={values.prenom}
                      onChange={handleChanges}
                      placeholder=" "
                      required
                    />

                    <label>Prénom</label>
                  </div>

                  {/* GENDER */}
                  <fieldset>
                    <legend>Sexe</legend>

                    <label>
                      <input
                        type="radio"
                        name="gender"
                        value="masculin"
                        checked={values.gender === "masculin"}
                        onChange={handleChanges}
                      />
                      M
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="gender"
                        value="feminin"
                        checked={values.gender === "feminin"}
                        onChange={handleChanges}
                      />
                      F
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="gender"
                        value="Autre"
                        checked={values.gender === "Autre"}
                        onChange={handleChanges}
                      />
                      Autre
                    </label>
                  </fieldset>

                  {/* DATE NAISSANCE */}
                  <div
                    className="input-group birthdate-group"
                    style={{
                      display: "block",
                      position: "relative",
                      width: "100%",
                      marginBottom: "18px",
                    }}
                  >
                    <label
                      className="birthdate-label"
                      style={{
                        position: "static",
                        display: "block",
                        transform: "none",
                        inset: "auto",
                        width: "auto",
                        margin: "0 0 8px 0",
                        padding: 0,
                        background: "transparent",
                        lineHeight: 1.4,
                        pointerEvents: "auto",
                      }}
                    >
                      Date de naissance
                    </label>
                    <div
                      className="birthdate-fields"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1.5fr 1fr",
                        gap: "10px",
                        width: "100%",
                        alignItems: "stretch",
                      }}
                    >
                      {/* DAY: independent dropdown */}
                      <select
                        aria-label="Jour de naissance"
                        className="birthdate-day"
                        style={{
                          width: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                        }}
                        value={birthDay}
                        onChange={(e) =>
                          updateBirthdate(e.target.value, birthMonth, birthYear)
                        }
                      >
                        <option value="">Jour</option>
                        {Array.from(
                          { length: daysInSelectedMonth },
                          (_, index) => {
                            const day = String(index + 1).padStart(2, "0");
                            return (
                              <option key={day} value={day}>
                                {index + 1}
                              </option>
                            );
                          },
                        )}
                      </select>

                      {/* MONTH: independent dropdown */}
                      <select
                        aria-label="Mois de naissance"
                        style={{
                          width: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                        }}
                        value={birthMonth}
                        onChange={(e) =>
                          updateBirthdate(birthDay, e.target.value, birthYear)
                        }
                      >
                        <option value="">Mois</option>
                        {[
                          "Janvier",
                          "Février",
                          "Mars",
                          "Avril",
                          "Mai",
                          "Juin",
                          "Juillet",
                          "Août",
                          "Septembre",
                          "Octobre",
                          "Novembre",
                          "Décembre",
                        ].map((name, index) => (
                          <option
                            key={name}
                            value={String(index + 1).padStart(2, "0")}
                          >
                            {name}
                          </option>
                        ))}
                      </select>

                      {/* YEAR: independent dropdown */}
                      <select
                        aria-label="Année de naissance"
                        style={{
                          width: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                        }}
                        value={birthYear}
                        onChange={(e) =>
                          updateBirthdate(birthDay, birthMonth, e.target.value)
                        }
                      >
                        <option value="">Année</option>
                        {Array.from(
                          { length: 120 },
                          (_, index) => new Date().getFullYear() - index,
                        ).map((year) => (
                          <option key={year} value={String(year)}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    {birthdateError && (
                      <div
                        role="alert"
                        style={{
                          marginTop: "8px",
                          padding: "9px 12px",
                          border: "1px solid #dc2626",
                          borderRadius: "6px",
                          background: "#fef2f2",
                          color: "#991b1b",
                          fontSize: "0.9rem",
                          lineHeight: 1.4,
                        }}
                      >
                        ⚠️ {birthdateError}
                      </div>
                    )}
                  </div>

                  {/* EMAIL WITH DOMAIN SUGGESTIONS */}
                  <div className="input-group" style={{ position: "relative" }}>
                    <input
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChanges}
                      placeholder=" "
                      autoComplete="email"
                    />

                    <label>Email</label>

                    {(() => {
                      const email = values.email || "";
                      const atIndex = email.lastIndexOf("@");
                      const domainPart =
                        atIndex >= 0
                          ? email.slice(atIndex + 1).toLowerCase()
                          : null;
                      const domains = [
                        "gmail.com",
                        "hotmail.com",
                        "outlook.com",
                        "yahoo.com",
                        "yahoo.fr",
                        "icloud.com",
                        "live.com",
                        "orange.fr",
                        "free.fr",
                        "sfr.fr",
                        "laposte.net",
                        "wanadoo.fr",
                      ];
                      const suggestions =
                        atIndex > 0 && domainPart !== null
                          ? domains.filter(
                              (domain) =>
                                domain.startsWith(domainPart) &&
                                domain !== domainPart,
                            )
                          : [];

                      return suggestions.length > 0 ? (
                        <div
                          role="listbox"
                          aria-label="Suggestions de domaine email"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            background: "#fff",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                            overflow: "hidden",
                          }}
                        >
                          {suggestions.slice(0, 5).map((domain) => (
                            <button
                              key={domain}
                              type="button"
                              role="option"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() =>
                                setValues((prev) => ({
                                  ...prev,
                                  email: `${prev.email.slice(0, prev.email.lastIndexOf("@") + 1)}${domain}`,
                                }))
                              }
                              style={{
                                display: "block",
                                width: "100%",
                                padding: "9px 12px",
                                border: 0,
                                background: "#fff",
                                textAlign: "left",
                                cursor: "pointer",
                                color: "#222",
                              }}
                            >
                              {email.slice(0, atIndex + 1)}
                              {domain}
                            </button>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* CONTACT */}
                  <div className="input-group">
                    <input
                      name="contact"
                      value={values.contact}
                      onChange={handleChanges}
                      placeholder=" "
                    />

                    <label>Contact</label>
                  </div>

                  {/* ADRESSE */}
                  <div className="input-group">
                    <input
                      name="adresse"
                      value={values.adresse}
                      onChange={handleChanges}
                      placeholder=" "
                    />

                    <label>Adresse</label>
                  </div>

                  {/* CODE POSTAL */}
                  <div className="input-group">
                    <input
                      name="codePostal"
                      value={values.codePostal}
                      onChange={handleChanges}
                      placeholder=" "
                    />

                    <label>Code Postal</label>
                  </div>

                  {/* COMMUNE */}
                  <div className="input-group">
                    <input
                      name="commune"
                      value={values.commune}
                      onChange={handleChanges}
                      placeholder=" "
                    />

                    <label>Commune</label>
                  </div>
                  {communeLookupMessage && (
                    <div
                      style={{
                        marginTop: "-8px",
                        marginBottom: "8px",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {communeLookupMessage}
                    </div>
                  )}
                  {communeSuggestions.length > 1 && (
                    <div
                      role="listbox"
                      aria-label="Communes correspondant au code postal"
                      style={{
                        marginTop: "-4px",
                        marginBottom: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        overflow: "hidden",
                        background: "#fff",
                      }}
                    >
                      {communeSuggestions.map((commune) => (
                        <button
                          key={commune}
                          type="button"
                          role="option"
                          aria-selected={values.commune === commune}
                          onClick={() => {
                            setValues((prev) => ({ ...prev, commune }));
                            setCommuneLookupMessage("");
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            padding: "9px 12px",
                            border: 0,
                            borderBottom: "1px solid #eee",
                            background:
                              values.commune === commune ? "#f3f3f3" : "#fff",
                            textAlign: "left",
                            cursor: "pointer",
                            color: "#222",
                          }}
                        >
                          {commune}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* METIER */}
                  <div className="input-group">
                    <input
                      name="metier"
                      value={values.metier}
                      onChange={handleChanges}
                      placeholder=" "
                    />

                    <label>Métier</label>
                  </div>

                  {/* HEBERGEMENT */}
                  <fieldset>
                    <legend>Besoin d'hébergement</legend>

                    <label>
                      <input
                        type="radio"
                        name="hebergement"
                        value="oui"
                        checked={values.hebergement === "oui"}
                        onChange={handleChanges}
                      />
                      Oui
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="hebergement"
                        value="non"
                        checked={values.hebergement === "non"}
                        onChange={handleChanges}
                      />
                      Non
                    </label>
                  </fieldset>

                  {/* TYPE ADHESION */}
                  <fieldset>
                    <legend>Type d’adhésion</legend>

                    <label>
                      <input
                        type="checkbox"
                        name="typeAdhesion"
                        value="adherent"
                        checked={values.typeAdhesion.includes("adherent")}
                        onChange={handleChanges}
                      />
                      Adhérent
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        name="typeAdhesion"
                        value="benevole"
                        checked={values.typeAdhesion.includes("benevole")}
                        onChange={handleChanges}
                      />
                      Bénévole
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        name="typeAdhesion"
                        value="organisateur"
                        checked={values.typeAdhesion.includes("organisateur")}
                        onChange={handleChanges}
                      />
                      Organisateur
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        name="typeAdhesion"
                        value="autre"
                        checked={values.typeAdhesion.includes("autre")}
                        onChange={handleChanges}
                      />
                      Autre
                    </label>
                  </fieldset>

                  {/* DATES */}
                  <label>
                    Date d'adhésion:
                    <ReactDatePicker
                      value={values.adhesion}
                      onChange={handleAdhesionChange}
                    />
                  </label>

                  <label>
                    Date d'expiration:
                    <ReactDatePicker value={values.dateExpiration} readOnly />
                  </label>

                  {/* COTISATION / PAYMENT */}
                  <fieldset>
                    <legend>Cotisation annuelle (facultative)</legend>

                    <p style={{ margin: "0 0 8px 0", fontWeight: "600" }}>
                      Montant annuel à payer (laisser vide si non applicable) :
                    </p>

                    <label>
                      <input
                        type="radio"
                        name="cotisation"
                        value="50"
                        checked={values.cotisation === "50"}
                        onChange={() => handleCotisationChange("50")}
                      />
                      50 € / an — Salarié
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="cotisation"
                        value="20"
                        checked={values.cotisation === "20"}
                        onChange={() => handleCotisationChange("20")}
                      />
                      20 € / an — Faibles ressources
                    </label>

                    {values.cotisation && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "10px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          background: "#fafafa",
                        }}
                      >
                        <strong>
                          Le membre a-t-il payé la totalité des{" "}
                          {values.cotisation} € ?
                        </strong>

                        <label style={{ marginTop: "8px" }}>
                          <input
                            type="radio"
                            name="cotisationStatus"
                            value="paye"
                            checked={values.cotisationStatus === "paye"}
                            onChange={() =>
                              handleCotisationStatusChange("paye")
                            }
                          />
                          Oui, payé en totalité
                        </label>

                        <label>
                          <input
                            type="radio"
                            name="cotisationStatus"
                            value="partiel"
                            checked={values.cotisationStatus === "partiel"}
                            onChange={() =>
                              handleCotisationStatusChange("partiel")
                            }
                          />
                          Non, paiement partiel
                        </label>

                        {values.cotisationStatus === "partiel" && (
                          <div style={{ marginTop: "10px" }}>
                            <label>
                              Montant payé :
                              <input
                                type="number"
                                name="cotisationAmount"
                                value={values.cotisationAmount}
                                onChange={handleChanges}
                                min="0"
                                max={values.cotisation}
                                step="0.01"
                                placeholder="Ex. 20"
                                style={{
                                  marginLeft: "8px",
                                  width: "120px",
                                }}
                              />{" "}
                              €
                            </label>

                            <div
                              style={{
                                marginTop: "8px",
                                fontWeight: "600",
                              }}
                            >
                              Reste à payer :{" "}
                              {getCotisationRemaining().toFixed(2)} €
                            </div>
                          </div>
                        )}

                        {values.cotisationStatus === "paye" && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontWeight: "600",
                            }}
                          >
                            Montant payé : {values.cotisation} € — Reste à payer
                            : 0 €
                          </div>
                        )}
                      </div>
                    )}
                  </fieldset>

                  {/* PHOTO */}
                  <div className="photo-field">
                    <label>📷 Photo</label>

                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      ref={photoRef}
                      name="photo"
                      onChange={handleChanges}
                    />

                    {/* CAMERA BUTTONS */}
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                        marginTop: "8px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => startCamera(videoRef, setCameraOn)}
                      >
                        📷 Ouvrir la camera
                      </button>

                      <button type="button" onClick={openPhoneCamera}>
                        📱 QR code
                      </button>
                    </div>

                    {/* CAMERA VIEW */}
                    {cameraOn && (
                      <div className="camera-box">
                        <video ref={videoRef} autoPlay playsInline />

                        <div className="camera-actions">
                          <button
                            type="button"
                            onClick={() =>
                              capturePhoto({
                                videoRef,
                                canvasRef,
                                setCameraOn,

                                onCapture: ({ file, preview }) => {
                                  setCropPhoto({
                                    file,
                                    preview,
                                    name: file?.name || "camera-photo.jpg",
                                  });

                                  setCrop(null);
                                },
                              })
                            }
                          >
                            📷 Capturer
                          </button>

                          <button
                            type="button"
                            onClick={() => stopCamera(videoRef, setCameraOn)}
                          >
                            ❌ Fermer
                          </button>
                        </div>

                        <canvas
                          ref={canvasRef}
                          style={{
                            display: "none",
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* PHOTO PREVIEW */}
                  {photoPreview && (
                    <div className="photo-preview">
                      <img src={photoPreview} alt="preview" />

                      <button
                        type="button"
                        onClick={() => {
                          setValues((prev) => ({
                            ...prev,
                            photo: null,
                          }));

                          setPhotoPreview(null);

                          if (photoRef.current) {
                            photoRef.current.value = null;
                          }
                        }}
                      >
                        Supprimer
                      </button>
                    </div>
                  )}

                  {/* SUIVI ADMINISTRATIF */}
                  <div className="form-group">
                    <label>📋 Suivi administratif</label>

                    <div
                      className="form-display"
                      onClick={() => setShowSuiviModal(true)}
                    >
                      {suivi.length > 0 ? suivi.join(" • ") : "Choisir"}
                    </div>
                  </div>

                  {/* SUIVI POPUP */}
                  {showSuiviModal && (
                    <div
                      className="modal-overlay"
                      onClick={() => setShowSuiviModal(false)}
                    >
                      <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h3>📋 Suivi administratif</h3>

                        <div className="suivi-container">
                          {[
                            "Demandeur d'asile - procédure normale",
                            "Demandeur d'asile - procédure accélérée",
                            "Demandeur d'asile - procédure Dublin",
                            "Sans papiers - pas de démarche en cours",
                            "Sans papiers - demande déposée en ligne",
                            "Sans papiers - demande déposée en préfecture",
                            "Situation régulière",
                            "Situation régulière - renouvellement en cours",
                            "OQTF en cours",
                            "IRTF en cours",
                          ].map((option) => {
                            const isActive = suivi.includes(option);

                            return (
                              <div
                                key={option}
                                className={`suivi-pill ${
                                  isActive ? "active" : ""
                                }`}
                                onClick={() => {
                                  if (isActive) {
                                    setSuivi(suivi.filter((s) => s !== option));
                                  } else {
                                    setSuivi([...suivi, option]);
                                  }
                                }}
                              >
                                {option}
                              </div>
                            );
                          })}
                        </div>

                        <button
                          className="btn primary"
                          onClick={() => setShowSuiviModal(false)}
                        >
                          ✅ Valider
                        </button>
                      </div>
                    </div>
                  )}

                  {/* COMMENTAIRE */}
                  <div className="input-group">
                    <textarea
                      name="commentaire"
                      value={values.commentaire}
                      onChange={handleChanges}
                      placeholder=" "
                      rows="4"
                    />

                    <label>Commentaire</label>
                  </div>

                  {/* NUMERO DOSSIER */}
                  <div className="input-group">
                    <input
                      type="text"
                      name="numero_dossier"
                      value={values.numero_dossier}
                      onChange={handleChanges}
                      placeholder=" "
                    />

                    <label>Numéro dossier</label>
                  </div>

                  {/* SIGNATURE */}
                  <div className="signature-section">
                    <label>✍️ Signature du membre</label>

                    <SignatureCanvas
                      ref={sigRef}
                      penColor="black"
                      onBegin={() => {
                        setMemberSignaturePreview(null);
                      }}
                      canvasProps={{
                        width: 350,
                        height: 150,
                        style: {
                          border: "2px solid #ccc",
                          borderRadius: "8px",
                          background: "#fff",
                        },
                      }}
                    />

                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                        marginTop: "10px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          sigRef.current?.clear();
                          setMemberSignaturePreview(null);
                          setValues((prev) => ({
                            ...prev,
                            signature: null,
                          }));
                        }}
                      >
                        ❌ Effacer
                      </button>

                      <button
                        type="button"
                        onClick={() => openPhoneCamera("signature")}
                      >
                        📄 Photographier une signature papier
                      </button>
                    </div>

                    {memberSignaturePreview && (
                      <div style={{ marginTop: "12px" }}>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            marginBottom: "6px",
                          }}
                        >
                          Signature papier numérisée :
                        </div>

                        <div
                          style={{
                            display: "inline-block",
                            padding: "10px",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            background:
                              "repeating-conic-gradient(#eee 0% 25%, #fff 0% 50%) 50% / 16px 16px",
                          }}
                        >
                          <img
                            src={memberSignaturePreview}
                            alt="Signature numérisée"
                            style={{
                              display: "block",
                              width: "350px",
                              maxWidth: "100%",
                              height: "auto",
                              maxHeight: "120px",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* BUTTONS */}
                  <div className="buttons">
                    <button type="button" onClick={resetForm}>
                      Reset
                    </button>

                    <button type="submit" disabled={!csrfToken}>
                      Submit
                    </button>
                  </div>
                </form>

                {/* RESPONSE */}
                {respValues.message && (
                  <div className={`alert ${respValues.type}`}>
                    {respValues.message}
                  </div>
                )}
              </div>
            </ProtectedAdminRoute>
          }
        />

        {/* PHONE CAMERA */}
        <Route path="/phone-camera" element={<PhoneCamera />} />

        {/* ADMIN */}
        <Route
          path="/admin"
          element={
            <ProtectedAdminRoute>
              <Admin />
            </ProtectedAdminRoute>
          }
        />

        {/* MEMBER PORTAL */}
        <Route
          path="/member-portal"
          element={
            <ProtectedMemberRoute>
              <MemberPortal />
            </ProtectedMemberRoute>
          }
        />

        {/* MEMBER PAGE */}
        <Route
          path="/member/:id"
          element={
            <ProtectedAdminRoute>
              <MemberPage />
            </ProtectedAdminRoute>
          }
        />

        {/* OLD LOGIN */}
        <Route path="/login" element={<MemberLogin />} />

        {/* SUPER ADMIN */}
        <Route
          path="/super-admin"
          element={
            <ProtectedSuperAdminRoute>
              <SuperAdmin />
            </ProtectedSuperAdminRoute>
          }
        />
      </Routes>
      <InstallAppPrompt />
      {/* PHONE CAMERA QR MODAL */}
      {showPhoneCamera && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            background: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={closePhoneCamera}
        >
          <div
            style={{
              width: "min(440px, 100%)",
              background: "#fff",
              borderRadius: "14px",
              padding: "24px",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>
              {phoneCameraPurpose === "signature"
                ? "✍️ Signature papier"
                : "📱 Caméra téléphone"}
            </h2>

            <p style={{ color: "#666", lineHeight: 1.5 }}>
              {phoneCameraPurpose === "signature"
                ? "Signez sur une feuille blanche, scannez ce QR code puis photographiez uniquement la signature. Elle sera automatiquement envoyée ici."
                : "Scannez ce QR code avec le téléphone. La photo prise sur le téléphone sera automatiquement envoyée ici."}
            </p>

            {phoneCameraQr && (
              <img
                src={phoneCameraQr}
                alt="QR code caméra téléphone"
                style={{
                  width: "260px",
                  height: "260px",
                  maxWidth: "100%",
                  display: "block",
                  margin: "18px auto",
                }}
              />
            )}

            <p
              style={{
                fontWeight: 600,
                color: "#333",
                minHeight: "24px",
              }}
            >
              {phoneCameraStatus}
            </p>

            <button
              type="button"
              onClick={closePhoneCamera}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              ❌ Fermer
            </button>
          </div>
        </div>
      )}

      {/* SIGNATURE PAPER CROP MODAL */}
      {signaturePhoto && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10000,
              background: "rgba(0, 0, 0, 0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                width: "min(900px, 100%)",
                maxHeight: "95vh",
                overflowY: "auto",
                background: "#fff",
                borderRadius: "14px",
                padding: "24px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 8px",
                  fontSize: "22px",
                  color: "#222",
                }}
              >
                ✍️ Numériser la signature
              </h2>

              <p
                style={{
                  margin: "0 0 18px",
                  color: "#666",
                  fontSize: "14px",
                  lineHeight: 1.5,
                }}
              >
                Cadrez uniquement la signature. Le fond blanc du papier sera
                automatiquement supprimé et la signature sera convertie en PNG
                transparent.
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "#f3f3f3",
                  borderRadius: "10px",
                  padding: "15px",
                  minHeight: "250px",
                  overflow: "auto",
                }}
              >
                <ReactCrop
                  crop={signatureCrop}
                  onChange={(_, percentCrop) => setSignatureCrop(percentCrop)}
                  keepSelection
                  minWidth={30}
                  minHeight={20}
                >
                  <img
                    ref={signatureCropImageRef}
                    src={signaturePhoto.preview}
                    alt="Signature papier à recadrer"
                    onLoad={handleSignatureImageLoad}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "60vh",
                      display: "block",
                    }}
                  />
                </ReactCrop>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "20px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setSignaturePhoto(null);
                    setSignatureCrop(null);
                  }}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  ❌ Annuler
                </button>

                <button
                  type="button"
                  onClick={createDigitalSignature}
                  disabled={!signatureCrop}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "8px",
                    border: "none",
                    background: "#ff6600",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: signatureCrop ? "pointer" : "not-allowed",
                    opacity: signatureCrop ? 1 : 0.55,
                  }}
                >
                  ✅ Utiliser cette signature
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* PHOTO CROP MODAL */}
      {cropPhoto && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(0, 0, 0, 0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                width: "min(900px, 100%)",
                maxHeight: "95vh",
                overflowY: "auto",
                background: "#fff",
                borderRadius: "14px",
                padding: "24px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 8px",
                  fontSize: "22px",
                  color: "#222",
                }}
              >
                ✂️ Recadrer la photo
              </h2>

              <p
                style={{
                  margin: "0 0 18px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                Déplacez la photo et ajustez le cadre. Le format est verrouillé
                en 3:4 et l'image ne sera jamais étirée.
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "#f3f3f3",
                  borderRadius: "10px",
                  padding: "15px",
                  minHeight: "250px",
                  overflow: "auto",
                }}
              >
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  aspect={3 / 4}
                  keepSelection
                  minWidth={30}
                  minHeight={30}
                >
                  <img
                    ref={cropImageRef}
                    src={cropPhoto.preview}
                    alt="À recadrer"
                    onLoad={handleCropImageLoad}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "60vh",
                      display: "block",
                    }}
                  />
                </ReactCrop>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "20px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setCropPhoto(null);
                    if (photoRef.current) photoRef.current.value = null;
                  }}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  ❌ Annuler
                </button>

                <button
                  type="button"
                  onClick={createCroppedPhoto}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "8px",
                    border: "none",
                    background: "#ff6600",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  ✅ Valider le recadrage
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default App;
